import { Complaint, ComplaintPosition, Workflow } from "@icc/b2b/shared";
import { core } from "@icc/shared/helpers";
import { ComplaintLevel, ComplaintTrigger, ComplaintTriggerParams } from "libs/b2b/shared/src/lib/models/complaint-workflow-rule";

const VALID_LEVELS_BY_CREATOR: Record<string, ComplaintLevel[]> = {
    client: ['authorizedRetailer', 'distributor', 'manufacturer'],
    client_distributor: ['clientDistributor', 'manufacturer'],
    authorized_retailer: ['distributor', 'manufacturer'],
    distributor: ['manufacturer'],
};

const VALID_LEVELS_FOR_ASSIGN: Record<string, ComplaintLevel[]> = {
    client: ['authorizedRetailer', 'distributor', 'manufacturer'],
    client_distributor: ['distributor', 'manufacturer'],
    authorized_retailer: ['authorizedRetailer','distributor', 'manufacturer'],
    distributor: ['distributor', 'manufacturer'],
};

export class ComplaintWorkflowService {
    static matchRules(positionsWorkflow: Workflow, trigger: ComplaintTrigger, document: Complaint, params: ComplaintTriggerParams) {
        const validLevels = VALID_LEVELS_BY_CREATOR[positionsWorkflow?.complaintCreator] || [];

        return positionsWorkflow?.rules.filter((el) => {
            // Filter out rules with levels not valid for the workflow's complaintCreator
            if (el.triggerParams.level && !validLevels.includes(el.triggerParams.level)) {
                return false;
            }
            if (el.trigger !== trigger) {
                return false;
            }
            if (trigger === 'button_click') {
                return el.triggerParams.button === params.button;
            }
            if (trigger === 'protocol_confirmed') {
                return el.triggerParams.protocolStatus === params.protocolStatus && el.triggerParams.level === params.level;
            }
            if (trigger === 'status_change') {
                return +el.triggerParams.documentStatus === +params.documentStatus;
            }
            if (trigger === 'schedule_appointment') {
                return el.triggerParams.appointmentStatus === params.appointmentStatus && el.triggerParams.level === params.level;
            }
            if (trigger === 'assigned_subject_change') {
                const fromLevelMatch = !el.triggerParams.fromLevel || (el.triggerParams.fromLevel === params.fromLevel && VALID_LEVELS_FOR_ASSIGN[positionsWorkflow.complaintCreator].includes(el.triggerParams.fromLevel));
                const toLevelMatch = !el.triggerParams.toLevel || (el.triggerParams.toLevel === params.toLevel && VALID_LEVELS_FOR_ASSIGN[positionsWorkflow.complaintCreator].includes(el.triggerParams.toLevel));
                return fromLevelMatch && toLevelMatch;
            }
            return true;
        }).filter(rule => {
            let isValid = true;
            for (const condition of rule.conditions) {
                const statusSetAuto = document[
                    (condition.params.level + 'StatusAuto') as 'authorizedRetailerStatusAuto' | 'distributorStatusAuto' | 'manufacturerStatusAuto'
                ];
                const statusId = document[
                    (condition.params.level + 'StatusId') as 'authorizedRetailerStatusId' | 'distributorStatusId' | 'manufacturerStatusId'
                ] || 0;
                if (condition.condition === 'status_set_auto' && !statusSetAuto) {
                    isValid = false;
                }
                if (condition.condition === 'status_set_manually' && statusSetAuto) {
                    isValid = false;
                }
                if (condition.condition === 'historical_status' && !(document.statusHistory || []).map(Number).includes(+condition.params.documentStatus!)) {
                    isValid = false;
                }
                if (condition.condition === 'not_historical_status' && (document.statusHistory || []).map(Number).includes(+condition.params.documentStatus!)) {
                    isValid = false;
                }
                if (condition.condition === 'current_status' && +statusId !== +condition.params.documentStatus!) {
                    isValid = false;
                }
            }
            return isValid;
        });

    }

    static getPositionWorkflow(
        document: Complaint,
        conf: ComplaintPosition['configuration'],
        workflows: Workflow[],
        workflow: Workflow | null = null
    ): Workflow | null {
        let complaintCreator = 'client';

        // Priority order for determining complaint creator type
        if (document.clientName?.length > 0 && document.authorizedRetailerCompanyId == null && document.distributorCompanyId != null) {
            // B2C complaint with distributor selected (not APS) - new workflow type
            complaintCreator = 'client_distributor';
        } else if (document.clientName?.length > 0) {
            // B2C complaint (may or may not have APS/distributor)
            complaintCreator = 'client';
        } else if (document.authorizedRetailerCompanyId != null) {
            // B2B complaint from APS
            complaintCreator = 'authorized_retailer';
        } else if (document.distributorCompanyId != null) {
            // B2B complaint from distributor
            complaintCreator = 'distributor';
        }

        const matchingWorkflow = workflows.find(w =>
            w.complaintCategories.includes(conf.id) &&
                w.documentTypeId === +document.documentTypeId &&
                w.complaintCreator === complaintCreator
        );
        if (matchingWorkflow) {
            workflow = core.copy(matchingWorkflow);
        }
        if (conf.children?.id) {
            return this.getPositionWorkflow(document, conf.children, workflows, workflow);
        } else {
            return workflow;
        }
    }

}
